import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card, Input } from 'reactstrap';
import { Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HubIcon from '@mui/icons-material/OtherHouses';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import { ACCOUNT_LOIGN_RETRY_COUNT } from 'src/utils/pnd/PndConstants';

const USER_COLUMNS = [
  {
    key: 'fullName',
    header: 'Full Name',
    sortable: true,
  },
  {
    key: 'userName',
    header: 'User Name',
    sortable: true,
  },
  {
    key: 'roleName',
    header: 'Role',
    sortable: true,
    render: (value, row) => row.userRoleInfo?.name || value || '—'
  },
  {
    key: 'hubName',
    header: 'Hub',
    sortable: true,
    render: (value, row) => row.hubName?.name || value || '—'
  },
  // {
  //   key: 'active',
  //   header: 'Status',
  //   width: '100px',
  //   align: 'center',
  //   sortable: false,
  //   render: (value) => (
  //     <span
  //       className={`badge ${value ? 'bg-success' : 'bg-danger'}`}
  //       style={{ fontSize: '0.72rem', fontWeight: 700 }}
  //     >
  //       {value ? 'ACTIVE' : 'IN-ACTIVE'}
  //     </span>
  //   ),
  // },
];

const UserListTableView = ({
  reactTableData,
  isNoRecordFound,
  onAdd,
  onEdit,
  onResetPassword,
  onUnlock
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedUser, setSelectedUser] = useState(
    rows.length > 0 ? rows[0] : null
  );

  const [selectedHub, setSelectedHub] = useState('all');

  // Extract unique hubs from full rows
  const uniqueHubs = useMemo(() => {
    const hubsMap = new Map();
    rows.forEach(row => {
      const name = typeof row.hubName === 'object'
        ? row.hubName?.name
        : (row.hubName || (row.hubInfo ? row.hubInfo.hubName : null));
      const id = row.hubId || (typeof row.hubName === 'object' ? row.hubName?.id || row.hubName?.hubId : null) || (row.hubInfo ? row.hubInfo.hubId : null);

      if (name) {
        const key = id ? String(id) : name;
        hubsMap.set(key, name);
      }
    });
    return Array.from(hubsMap.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  // Filter rows based on selectedHub
  const filteredRows = useMemo(() => {
    if (selectedHub === 'all') return rows;
    return rows.filter(row => {
      const name = typeof row.hubName === 'object'
        ? row.hubName?.name
        : (row.hubName || (row.hubInfo ? row.hubInfo.hubName : null));
      const id = row.hubId || (typeof row.hubName === 'object' ? row.hubName?.id || row.hubName?.hubId : null) || (row.hubInfo ? row.hubInfo.hubId : null);

      const rowHubKey = id ? String(id) : name;
      return String(rowHubKey) === String(selectedHub);
    });
  }, [rows, selectedHub]);

  // Reset selectedUser when filteredRows changes
  useEffect(() => {
    if (filteredRows.length > 0) {
      setSelectedUser(prevSelected => {
        const exists = filteredRows.some(r => r.userId === prevSelected?.userId);
        return exists ? prevSelected : filteredRows[0];
      });
    } else {
      setSelectedUser(null);
    }
  }, [filteredRows]);

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          <RadioSelectTable
            rows={filteredRows}
            columns={USER_COLUMNS}
            rowKey="userId"
            selectedKey={selectedUser?.userId}
            onSelect={setSelectedUser}
            searchKeys={['fullName', 'userName', 'emailId', 'mobileNo', 'roleName']}
            searchPlaceholder="Search by Name, Username, Email, Role..."
            sortKey="fullName"
            headerTitle="User Accounts"
            onAdd={onAdd}
            pageSizes={[5, 10, 20, 50]}
            filterDropdown={
              uniqueHubs.length > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Hub:</span>
                  <Input
                    type="select"
                    value={selectedHub}
                    onChange={(e) => setSelectedHub(e.target.value)}
                    style={{
                      height: '38px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.85rem',
                      color: '#1e293b',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '160px'
                    }}
                  >
                    <option value="all">All Hubs</option>
                    {uniqueHubs.map(hub => (
                      <option key={hub.value} value={hub.value}>{hub.label}</option>
                    ))}
                  </Input>
                </div>
              )
            }
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedUser ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <AccountCircleIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedUser.fullName}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          @{selectedUser.userName}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        className={`badge ${selectedUser.active ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {selectedUser.active ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                      
                      {selectedUser.retryCount >= ACCOUNT_LOIGN_RETRY_COUNT && (
                        <Tooltip title="Unlock locked user account">
                          <IconButton
                            size="small"
                            onClick={() => onUnlock(selectedUser)}
                            style={{ color: '#ef4444', backgroundColor: 'rgba(255,255,255,0.15)' }}
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Edit details">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(selectedUser)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => onResetPassword(selectedUser)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Account specifications block */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Account Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Client Name:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BusinessIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedUser.customerInfo?.clientName || selectedUser.customerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">User Role:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BadgeIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedUser.userRoleInfo?.name || selectedUser.roleName || '—'}
                          </span>
                        </div>
                        {selectedUser.hubName && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Assigned Hub:</span>
                            <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                              <HubIcon style={{ fontSize: '14px', color: '#64748b' }} />
                              {selectedUser.hubName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact details block */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Contact Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Email Address:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <EmailIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedUser.emailId || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Mobile No:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <PhoneIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedUser.mobileNo || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>👥</div>
                <h5 className="fw-bold text-muted">No User Selected</h5>
                <p className="text-muted small">Select a user account from the list to view specifications.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

UserListTableView.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  onUnlock: PropTypes.func.isRequired,
};

export default UserListTableView;
